<script lang="ts">
  import PageSection from '@athena-lib/Page/PageSection.svelte';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { Column, Columns } from '@athena-lib/Columns';
  import { HeadingSmall, Text } from '@athena-lib/Text';
  import { Image } from '@athena-lib/Image';
  import CloudUpload from '@athena-lib/Icons/CloudUpload.svelte';
  import { Button } from '@athena-lib/Button';
  import { Delete, Spinner } from '@athena-lib/Icons';
  import { Modal } from '@athena-lib/Modal';
  import AccountTabs from './AccountTabs.svelte';
  import Box from './Box.svelte';
  import type { TaxData } from '$lib/server/external/types';

  const translations = getCommonTranslations();

  const { data }: { data: TaxData } = $props();
  let fileInput: HTMLInputElement | undefined = $state();
  let uploadState = $state<null | 'uploading' | 'success' | 'badRequest' | 'entityTooLarge'>();
  async function onUploadFile(e: Event) {
    e.preventDefault();
    const file = fileInput?.files?.[0];
    if (file && fileInput) {
      const formData = new FormData();
      formData.append('attachment_type', '14');
      formData.append('user_id', data.accountData.userInfo.user_id);
      formData.append('lang', data.accountData.billing.country_code as string);
      formData.append('user_nonce', data.accountData.userInfo.nonce);

      formData.append('files[0]', file);

      uploadState = 'uploading';
      const result = await fetch('/customer/ajax_tax_certificate_upload', {
        method: 'POST',
        body: formData,
      });
      fileInput.value = '';

      switch (result.status) {
        case 200:
          uploadState = 'success';
          break;
        case 413:
          uploadState = 'entityTooLarge';
          break;
        case 400:
          uploadState = 'badRequest';
          break;
        default:
          uploadState = null;
          break;
      }
    }
  }

  async function deleteByHash(hash: string) {
    const formData = new FormData();
    formData.append('user_nonce', data.accountData.userInfo.nonce);
    formData.append('attachment_hash', hash);

    const result = await fetch('/customer/ajax_delete_attachment_by_hash', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
    if (result.status === 200) {
      const index = data.uploadedCertificates.files.findIndex((c) => c.hash === hash);
      if (index >= 0) {
        data.uploadedCertificates.files.splice(index, 1);
      }
    }
  }

  const modalHeading = $derived(() => {
    switch (uploadState) {
      case 'success':
        return {
          heading: translations.taxUploadSuccessTitle,
          description: translations.taxUploadSuccessDescription,
        };
      case 'entityTooLarge':
      case 'badRequest':
        return {
          heading: translations.generalError,
          description: translations.anErrorHasOccured,
        };
      default:
        break;
    }
  });
</script>

<AccountTabs currentPage="taxCertificates" market={data.market} accountData={data.accountData}
></AccountTabs>
<PageSection class="grow" backgroundColor="medium">
  <HeadingSmall>{translations.taxCertificates}</HeadingSmall>
  <hr class="mb-4 mt-3" />
  <Columns collapseBelow="md">
    <Column>
      <Box>
        <Stack>
          <StackItem>
            <Columns space="large">
              <Column>
                <Text class="font-bold" element="p">{translations.approvedTaxCertificate}</Text>
              </Column>
              {#if data.accountData.currentTaxCertificate}
                <Column>
                  <Text class="w-1/4" element="span"
                    >{translations.expirationDate}
                    {data.accountData.currentTaxCertificate.expirationDate}</Text
                  >
                  <a
                    href={data.accountData.currentTaxCertificate.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      imgClass="h-64 w-64"
                      src={data.accountData.currentTaxCertificate.url ?? ''}
                      alt={''}
                      loading="lazy"
                    ></Image>
                  </a>
                </Column>
              {/if}
            </Columns>
          </StackItem>
          <StackItem>
            <hr />
          </StackItem>
          <StackItem>
            <Columns space="large">
              <Column>
                <Text class="font-bold" element="p">{translations.uploadedFiles}</Text>
              </Column>
              <Column>
                <Stack>
                  {#each data.uploadedCertificates.files as file}
                    <Box>
                      <StackItem class="mb-3">
                        <a href={file.url} target="_blank" rel="noopener noreferrer">
                          <Image imgClass="max-h-40" src={file.url ?? ''} alt={''} loading="lazy"
                          ></Image>
                        </a>
                      </StackItem>
                      <StackItem class="mb-3">
                        <hr />
                      </StackItem>
                      <StackItem>
                        <Button
                          color="white"
                          class="flex content-center items-center justify-center gap-2 rounded-lg"
                          onClick={() => deleteByHash(file.hash)}
                          full
                          size="small"
                        >
                          <Delete sizeClass="h-6 w-6"></Delete> Delete</Button
                        >
                      </StackItem>
                    </Box>
                  {/each}
                </Stack>
              </Column>
            </Columns>
          </StackItem>
        </Stack>
      </Box>
    </Column>
    <Column class="!h-1/2">
      <Box>
        <Stack>
          <StackItem>
            <Columns space="large">
              <Column>
                <Text element="p">{translations.taxExemptBusiness}</Text>
              </Column>
              <Column>
                <input
                  bind:this={fileInput}
                  onchange={onUploadFile}
                  class="hidden"
                  type="file"
                  name="files[]"
                  accept="image/jpeg, image/png, image/gif, application/pdf, application/postscript, image/svg+xml, image/bmp, image/x-windows-bmp, application/psd, application/x-photoshop, image/photoshop, image/psd, image/x-photoshop, image/x-psd, .psd, image/tiff"
                />
                <Button
                  disabled={uploadState === 'uploading'}
                  color="white"
                  class="grid justify-center rounded-lg"
                  onClick={() => fileInput?.click()}
                  full={true}
                >
                  {#if uploadState === 'uploading'}
                    <Spinner sizeClass="w-20 h-20" />
                  {:else}
                    <CloudUpload></CloudUpload>
                  {/if}
                  <Text>{translations.upload}</Text>
                </Button>
              </Column>
            </Columns>
          </StackItem>
        </Stack>
      </Box>
    </Column>
  </Columns>
  <Modal size="sm" showModal={!!(uploadState && uploadState !== 'uploading')}>
    <Stack class="p-8" space="medium">
      <StackItem class="mb-8 text-left">
        <HeadingSmall class="mb-4">{modalHeading()?.heading}</HeadingSmall>
        <Text element="p">{modalHeading()?.description}</Text>
      </StackItem>
      <StackItem class="flex justify-center">
        <Button
          onClick={() => {
            if (uploadState === 'success') {
              window.location.reload();
            }
            uploadState = null;
          }}
          size="small"
        >
          {translations.closeText}
        </Button>
      </StackItem>
    </Stack>
  </Modal>
</PageSection>
